// Address validation client / facade. The single public entrypoint
// for the address-validation primitive -- consumers import
// validateAddress() + the types from here, never the provider module
// directly. That gives us one place to swap providers (stub <->
// real vendor) without touching consumers.
//
// v1 ships with the stub provider (local heuristics, no network).
// A real-vendor follow-on lands a new module under
// src/lib/addressValidation/ that satisfies AddressValidationProvider
// and calls setActiveAddressValidationProvider() at app boot.
import { stubProvider } from "./addressValidation/stubProvider";
import type { AddressValidationProvider } from "./addressValidation/provider";
import type {
  AddressValidationResult,
  StructuredAddress,
} from "./addressValidation/types";

let activeProvider: AddressValidationProvider = stubProvider;

/**
 * Swap the active provider. Intended for app-boot configuration
 * (real vendor wiring) and for tests that need a deterministic
 * provider. Returns the previous provider so tests can restore.
 */
export function setActiveAddressValidationProvider(
  next: AddressValidationProvider
): AddressValidationProvider {
  const prev = activeProvider;
  activeProvider = next;
  return prev;
}

/**
 * Inspect the currently-active provider. Mostly for tests + the UI
 * affordance's "via {provider}" badge.
 */
export function getActiveAddressValidationProvider(): AddressValidationProvider {
  return activeProvider;
}

/**
 * Validate + normalize an address using the active provider. Never
 * throws for input-shape reasons; provider/network failures arrive
 * as "invalid" status with an error issue (see provider.ts contract).
 */
export async function validateAddress(
  input: StructuredAddress
): Promise<AddressValidationResult> {
  return activeProvider.validate(input);
}

// Re-export the type surface so consumers only need to import from
// this entrypoint.
export type {
  AddressValidationIssue,
  AddressValidationIssueSeverity,
  AddressValidationResult,
  AddressValidationStatus,
  StructuredAddress,
} from "./addressValidation/types";
export type { AddressValidationProvider } from "./addressValidation/provider";
